import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material'
import { NotificationImportant } from '@mui/icons-material'
import ApexLogo from '@/assets/apex-logo.svg'
import { amplifyConfig } from '@/config/amplify'
import { useAuth } from '@/hooks/useAuth'
import { toastMessage } from '@/constants/strings'

// Azure AD logo SVG component
const AzureADIcon = () => (
  <svg width="21" height="21" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1h9v9H1z" fill="#f25022"></path>
    <path d="M1 11h9v9H1z" fill="#00a4ef"></path>
    <path d="M11 1h9v9h-9z" fill="#7fba00"></path>
    <path d="M11 11h9v9h-9z" fill="#ffb900"></path>
  </svg>
)

export function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { signInWithAzureAD, isAuthenticated, isLoading, error, user } = useAuth()

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/apex-client'

  // Get environment from config
  const environment = amplifyConfig.environment.toUpperCase()
  const showEnvBadge = ['TST', 'DEV', 'QA', 'UAT'].includes(environment)

  // Check if we're coming back from OAuth redirect
  const isOAuthCallback = location.search.includes('code=') || location.hash.includes('access_token')

  useEffect(() => {
    if (isAuthenticated && user) {
      // User is authenticated, redirect to apex-client
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, user, navigate, from])

  const handleAzureADSignIn = async () => {
    try {
      await signInWithAzureAD()
    } catch {
      // Error is handled in AuthContext and displayed in the UI
    }
  }

  if (isLoading || (isOAuthCallback && !error)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          {isOAuthCallback ? toastMessage.login.completingSignIn : toastMessage.login.loading}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '98vh',
        justifyContent: 'center',
        backgroundColor: 'background.default',
      }}
    >
      <Card
        sx={{
          maxWidth: 600,
          width: '100%',
          height: 500,
          borderRadius: 2,
          boxShadow: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Logo Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 4,
              gap: 2,
            }}
          >
            <Box
              component="img"
              src={ApexLogo}
              alt="Apex - Advanced Planning & Execution"
              sx={{ height: 80, width: 'auto' }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationImportant sx={{ fontSize: 28, color: 'primary.main' }} />
              <Typography variant="h5" component="h2" fontWeight="bold">
                Issue Tracker Dashboard
              </Typography>
              {showEnvBadge && (
                <Box
                  sx={{
                    ml: 1,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: 'primary.light',
                    color: 'primary.dark',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  {environment}
                </Box>
              )}
            </Box>
          </Box>

          {/* Page Heading */}
          <Typography variant="h5" component="h1" textAlign="center" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            Sign in to your account
          </Typography>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* SSO Button */}
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={handleAzureADSignIn}
            disabled={isLoading}
            startIcon={<AzureADIcon />}
            sx={{
              mb: 3,
              py: 1.5,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            Sign in with Azure AD
          </Button>
        </CardContent>
      </Card>
    </Box>
  )
}
